import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, User, Loader2, Plus, Shield, CreditCard, Pencil, Eye, CheckSquare, Square } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { EditUserDialog } from '../../components/admin/EditUserDialog';
import { BatchActionsBar } from '../../components/admin/BatchActionsBar';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planColors = {
  free: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  starter: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  pro: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  team: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;
  const [editUser, setEditUser] = useState(null);
  const [editPlan, setEditPlan] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editCredits, setEditCredits] = useState('');
  const [saving, setSaving] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditUserId, setCreditUserId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map(u => u.id)));
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, {
        params: { skip: page * limit, limit },
        withCredentials: true,
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      logger.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [page, limit]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const batchAction = async (action) => {
    if (selected.size === 0) { toast.error('Select users first'); return; }
    const ids = Array.from(selected);
    try {
      if (action === 'suspend') {
        await axios.post(`${API_URL}/api/admin/batch/suspend`, { user_ids: ids }, { withCredentials: true });
        toast.success(`Suspended ${ids.length} users`);
      } else if (action === 'unsuspend') {
        await axios.post(`${API_URL}/api/admin/batch/unsuspend`, { user_ids: ids }, { withCredentials: true });
        toast.success(`Unsuspended ${ids.length} users`);
      } else if (action === 'credits') {
        const amt = prompt('Credits to add:');
        if (!amt) return;
        await axios.post(`${API_URL}/api/admin/batch/credits`, { user_ids: ids, amount: parseInt(amt) }, { withCredentials: true });
        toast.success(`Added ${amt} credits to ${ids.length} users`);
      }
      setSelected(new Set());
      fetchUsers();
    } catch (err) {
      logger.error('Batch action failed:', err);
      toast.error('Batch action failed');
    }
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const body = {};
      if (editPlan && editPlan !== editUser.plan) body.plan = editPlan;
      if (editRole && editRole !== editUser.role) body.role = editRole;
      if (editCredits !== '' && parseInt(editCredits) !== editUser.credits_remaining) body.credits_remaining = parseInt(editCredits);
      if (Object.keys(body).length === 0) { toast.info('No changes'); setSaving(false); return; }
      await axios.put(`${API_URL}/api/admin/users/${editUser.id}`, body, { withCredentials: true });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      logger.error('Update user failed:', err);
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleAddCredits = async (userId) => {
    const amt = parseInt(creditAmount);
    if (!amt || amt <= 0) { toast.error('Enter a positive number'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/credits?amount=${amt}`, {}, { withCredentials: true });
      toast.success(`Added ${amt} credits`);
      setCreditUserId(null);
      setCreditAmount('');
      fetchUsers();
    } catch (err) {
      logger.error('Failed to add credits:', err);
      toast.error('Failed to add credits');
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditPlan(user.plan || 'free');
    setEditRole(user.role || 'user');
    setEditCredits(String(user.credits_remaining || 0));
  };

  const filteredUsers = users.filter((u) => {
    const matchSearch = search === '' || u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase());
    const matchPlan = planFilter === 'all' || u.plan === planFilter;
    return matchSearch && matchPlan;
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Users</h1>
        <p className="text-white/45 text-sm">Manage all SendPI users &middot; {total} total</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name..."
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25" data-testid="user-search-input" />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Filter by plan" /></SelectTrigger>
          <SelectContent className="bg-[#0E1830] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/5">All plans</SelectItem>
            <SelectItem value="free" className="text-white hover:bg-white/5">Free</SelectItem>
            <SelectItem value="starter" className="text-white hover:bg-white/5">Starter</SelectItem>
            <SelectItem value="pro" className="text-white hover:bg-white/5">Pro</SelectItem>
            <SelectItem value="team" className="text-white hover:bg-white/5">Team</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <BatchActionsBar selected={selected} onAction={batchAction} onClear={() => setSelected(new Set())} />
      )}

      <div className="bg-[#0E1830] border border-white/5 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-white/40 animate-spin" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-4 px-3 w-10">
                      <button onClick={toggleAll} className="text-white/30 hover:text-white" data-testid="select-all">
                        {selected.size === users.length ? <CheckSquare className="w-4 h-4 text-cyan-400" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="text-left py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">User</th>
                    <th className="text-left py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">Plan</th>
                    <th className="text-left py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">Credits</th>
                    <th className="text-left py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">Role</th>
                    <th className="text-left py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">Joined</th>
                    <th className="text-right py-4 px-5 text-white/40 font-space font-bold text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id || user.email} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-3">
                        <button onClick={() => toggleSelect(user.id)} className="text-white/30 hover:text-white">
                          {selected.has(user.id) ? <CheckSquare className="w-4 h-4 text-cyan-400" /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full pi-gradient flex items-center justify-center text-white text-xs font-bold">
                            {user.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{user.name || 'No name'}</p>
                            <p className="text-white/30 text-xs">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <span className={`px-2.5 py-1 text-xs rounded-full border font-semibold ${planColors[user.plan] || planColors.free}`}>
                          {(user.plan || 'free').toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">{(user.credits_remaining || 0).toLocaleString()}</span>
                          <span className="text-white/25 text-xs">/ {(user.credits_total || 100).toLocaleString()}</span>
                          {creditUserId === user.id ? (
                            <div className="flex items-center gap-1 ml-2">
                              <Input value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} type="number" placeholder="Amount"
                                className="w-20 h-7 text-xs bg-white/5 border-white/10 text-white" />
                              <Button size="sm" onClick={() => handleAddCredits(user.id)}
                                className="h-7 px-2 text-xs shimmer-btn text-white border-0">Add</Button>
                              <Button size="sm" variant="ghost" onClick={() => setCreditUserId(null)}
                                className="h-7 px-2 text-xs text-white/40">X</Button>
                            </div>
                          ) : (
                            <button onClick={() => { setCreditUserId(user.id); setCreditAmount(''); }}
                              className="ml-1 text-[var(--pi-cyan)] hover:text-[var(--pi-cyan)]/80" data-testid={`add-credits-${user.id}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <span className={`text-sm font-medium ${user.role === 'admin' ? 'text-red-400' : 'text-white/50'}`}>
                          {user.role === 'admin' && <Shield className="w-3.5 h-3.5 inline mr-1" />}
                          {user.role || 'user'}
                        </span>
                      </td>
                      <td className="py-3 px-5 text-white/40 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-3 px-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="border-white/10 text-cyan-400 hover:bg-cyan-500/10 rounded-full px-3 text-xs"
                            data-testid={`view-user-${user.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/impersonate/${user.id}`)}
                            className="border-white/10 text-amber-400 hover:bg-amber-500/10 rounded-full px-3 text-xs"
                            data-testid={`impersonate-${user.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> As User
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(user)}
                            className="border-white/10 text-white/50 hover:bg-white/5 rounded-full px-3 text-xs"
                            data-testid={`edit-user-${user.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t border-white/[0.04]">
              <p className="text-white/30 text-sm">{page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="border-white/10 text-white hover:bg-white/5"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-white/40 text-sm px-2">Page {page + 1} / {totalPages || 1}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                  className="border-white/10 text-white hover:bg-white/5"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </div>

      <EditUserDialog
        editUser={editUser} setEditUser={setEditUser}
        editPlan={editPlan} setEditPlan={setEditPlan}
        editRole={editRole} setEditRole={setEditRole}
        editCredits={editCredits} setEditCredits={setEditCredits}
        saving={saving} onSave={handleEditUser}
      />
    </div>
  );
}
